import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faUser } from '@fortawesome/free-solid-svg-icons';
import { TEST_SERVER_FILE_NOTE } from '../constants/uploadMessages';

export function UploadMissingNote({ style = {} }) {
  return (
    <div className="no-print" style={{
      marginTop: '10px',
      maxWidth: '420px',
      padding: '10px 12px',
      borderRadius: '10px',
      background: '#fffbeb',
      border: '1px solid #fcd34d',
      textAlign: 'left',
      ...style,
    }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: '8px',
        fontSize: '12px', color: '#92400e', lineHeight: 1.45,
      }}>
        <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginTop: '2px', color: '#f59e0b', flexShrink: 0 }} />
        <span>{TEST_SERVER_FILE_NOTE}</span>
      </div>
    </div>
  );
}

/**
 * Renders an uploaded image URL. If the file is missing (e.g. after a test-server
 * redeploy), shows a warning state instead of a broken image.
 */
export default function UploadedImage({
  src,
  alt = 'Uploaded image',
  emptyIcon = faUser,
  imageStyle = {},
  emptyIconStyle = { fontSize: '52px', color: '#d1d5db' },
  onUnavailableChange,
}) {
  const [status, setStatus] = useState(src ? 'loading' : 'empty'); // empty | loading | ok | missing

  useEffect(() => {
    if (!src) {
      setStatus('empty');
      onUnavailableChange?.(false);
      return undefined;
    }

    if (String(src).startsWith('blob:') || String(src).startsWith('data:')) {
      setStatus('ok');
      onUnavailableChange?.(false);
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');
    onUnavailableChange?.(false);

    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setStatus('ok');
        onUnavailableChange?.(false);
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setStatus('missing');
        onUnavailableChange?.(true);
      }
    };
    img.src = src;

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  if (status === 'missing') {
    return (
      <FontAwesomeIcon
        icon={faExclamationTriangle}
        style={{ fontSize: '40px', color: '#f59e0b' }}
        title="File is no longer available"
      />
    );
  }

  if (status === 'ok' || status === 'loading') {
    if (!src) {
      return <FontAwesomeIcon icon={emptyIcon} style={emptyIconStyle} />;
    }
    return (
      <img
        src={src}
        alt={alt}
        style={imageStyle}
        onError={() => {
          setStatus('missing');
          onUnavailableChange?.(true);
        }}
        onLoad={() => {
          setStatus('ok');
          onUnavailableChange?.(false);
        }}
      />
    );
  }

  return <FontAwesomeIcon icon={emptyIcon} style={emptyIconStyle} />;
}
